import { useMemo, useCallback, useState } from 'react';
import { useGetLibraryArticlesQuery } from 'services/resourceService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { TransformedLibraryArticle } from '../types';
import { getArticleImageForUser } from '../utils';
import { LIBRARY_PAGE_SIZE } from '../utils/contentSearchUtils';

/**
 * Paginated library articles. All pages share one RTK Query cache entry and page 0 resets it, so
 * only mount this where the list is actually shown (never in two live components at once).
 */
export const useLibraryArticles = () => {
  const user = useLoggedInUser();
  const memberUuid = user?.uuid;
  const [libraryPage, setLibraryPage] = useState(0);

  const {
    data,
    isLoading: isLoadingLibraryArticles,
    isFetching: isFetchingLibraryArticles,
    error: libraryArticlesError,
    refetch: refetchLibraryArticles,
  } = useGetLibraryArticlesQuery({ page: libraryPage });

  // A full page means more may exist (same rule as content search). Stay true while the next
  // page is fetching so the bottom spinner remains mounted.
  const hasMoreLibraryArticles =
    !!data && (isFetchingLibraryArticles || data.length >= (libraryPage + 1) * LIBRARY_PAGE_SIZE);

  const loadMoreLibraryArticles = useCallback(() => {
    if (hasMoreLibraryArticles && !isFetchingLibraryArticles) {
      setLibraryPage((previousPage) => previousPage + 1);
    }
  }, [hasMoreLibraryArticles, isFetchingLibraryArticles]);

  const libraryArticles = useMemo<TransformedLibraryArticle[]>(() => {
    if (!data || !memberUuid) {
      return [];
    }

    return data.map((article) => ({
      id: article.id,
      title: article.title,
      description: article.description,
      contentUrl: article.content_url,
      contentType: article.content_type,
      // Keyed by url (not id) so the same article gets the same image in search and favorites.
      imageUrl: getArticleImageForUser(memberUuid, article.content_url),
      isIndependent: article.is_independent,
      createdAt: article.created_at,
    }));
  }, [data, memberUuid]);

  // A failed later page keeps the loaded articles on screen and is retried inline instead.
  const isLoadMoreError = !!libraryArticlesError && !!data?.length;
  const isLibraryError = !!libraryArticlesError && !isLoadMoreError;

  return {
    libraryArticles,
    isLoadingLibraryArticles,
    isFetchingLibraryArticles,
    hasMoreLibraryArticles,
    loadMoreLibraryArticles,
    isLoadMoreError,
    isLibraryError,
    libraryArticlesError,
    refetchLibraryArticles,
  };
};
